import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Bell, HelpCircle, Menu, ChevronDown, Settings, ShieldCheck, LogOut, FolderKanban } from 'lucide-react';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { ALL_ITEMS } from './navConfig';

const BADGE_LABELS = {
  time_reports: ['Tidrapporter att granska', '/dashboard/time-reports'],
  eta: ['Nya ÄTA', '/dashboard/eta'],
  invoices: ['Fakturor att hantera', '/dashboard/invoices'],
  support_chat: ['Supportmeddelanden', '/dashboard/support-chat'],
  calendar: ['Kalenderhändelser', '/dashboard/calendar'],
};

const CommandSearch = ({ open, setOpen, projects }) => {
  const navigate = useNavigate();
  const go = (path) => { setOpen(false); navigate(path); };
  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Sök projekt, sidor, material…" data-testid="command-search-input" />
      <CommandList>
        <CommandEmpty>Inga träffar.</CommandEmpty>
        {projects.length > 0 && (
          <CommandGroup heading="Projekt">
            {projects.map((p) => (
              <CommandItem key={p.id} value={`projekt ${p.name} ${p.customer || ''}`} onSelect={() => go(`/dashboard/projects/${p.id}`)} data-testid={`command-project-${p.id}`}>
                <FolderKanban size={14} className="mr-2 text-[#F26B1F]" />{p.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Sidor">
          {ALL_ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <CommandItem key={it.path} value={`sida ${it.label}`} onSelect={() => go(it.path)}>
                <Icon size={14} className="mr-2 text-muted-foreground" />{it.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

const Notifications = ({ badges }) => {
  const entries = Object.entries(BADGE_LABELS).filter(([k]) => (badges[k] || 0) > 0);
  const total = entries.reduce((s, [k]) => s + (badges[k] || 0), 0);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative w-10 h-10 rounded-full flex items-center justify-center text-stone-600 hover:bg-stone-100 transition-colors" data-testid="topbar-notifications-btn" aria-label="Notiser">
          <Bell size={19} />
          {total > 0 && <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F26B1F] text-white text-[9px] font-bold flex items-center justify-center" data-testid="topbar-notifications-count">{total > 99 ? '99+' : total}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2 rounded-2xl" data-testid="topbar-notifications-panel">
        <p className="px-2 py-1.5 text-xs font-semibold text-stone-500">Notiser</p>
        {entries.length === 0 && <p className="px-2 py-4 text-sm text-stone-500">Allt är hanterat.</p>}
        {entries.map(([k, [label, path]]) => (
          <Link key={k} to={path} className="flex items-center justify-between rounded-xl px-2 py-2 text-sm hover:bg-stone-100" data-testid={`notification-${k}`}>
            <span>{label}</span><span className="text-xs font-bold text-[#F26B1F]">{badges[k]}</span>
          </Link>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const UserMenu = ({ user, companyName, onLogout }) => {
  const initials = (user?.name || user?.email || 'O').split(/[\s@.]/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2.5 rounded-full pl-1 pr-2 py-1 hover:bg-stone-100 transition-colors" data-testid="topbar-user-menu">
          <span className="w-9 h-9 rounded-full bg-[#1B1918] text-white text-xs font-bold flex items-center justify-center">{initials}</span>
          <span className="hidden md:block text-left leading-tight">
            <span className="block text-[13px] font-semibold text-stone-900" data-testid="user-name">{user?.name || user?.email}</span>
            <span className="block text-[11px] text-stone-500">{companyName || 'Admin'}</span>
          </span>
          <ChevronDown size={14} className="hidden md:block text-stone-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-xl">
        <DropdownMenuItem asChild><Link to="/dashboard/settings" data-testid="user-menu-settings"><Settings size={14} className="mr-2" />Inställningar</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/dashboard/security" data-testid="user-menu-security"><ShieldCheck size={14} className="mr-2" />Säkerhet</Link></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="logout-btn"><LogOut size={14} className="mr-2" />Logga ut</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Topbar = ({ title, onOpenSidebar, badges, user, companyName, onLogout, projects }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <header className="flex-shrink-0 bg-[#F4F2EE]/85 backdrop-blur-xl border-b border-stone-200/70 px-4 sm:px-6 flex items-center gap-3"
      style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(64px + env(safe-area-inset-top))' }} data-testid="admin-topbar">
      <button onClick={onOpenSidebar} className="lg:hidden w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-stone-200/60" data-testid="open-sidebar-btn" aria-label="Öppna meny"><Menu size={21} /></button>
      <h1 className="lg:hidden text-base font-bold truncate flex-1">{title}</h1>
      <button onClick={() => setOpen(true)} data-testid="topbar-search-btn"
        className="hidden lg:flex items-center gap-3 h-11 w-full max-w-[460px] rounded-xl bg-white border border-stone-200 px-4 text-sm text-stone-400 hover:border-stone-300 transition-colors">
        <Search size={17} /><span className="flex-1 text-left">Sök projekt, ritningar, material, påslag…</span>
        <kbd className="text-[11px] font-semibold text-stone-400 border border-stone-200 rounded-md px-1.5 py-0.5">⌘ K</kbd>
      </button>
      <div className="flex-1 hidden lg:block" />
      <button onClick={() => setOpen(true)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-stone-200/60" data-testid="topbar-search-btn-mobile" aria-label="Sök"><Search size={19} /></button>
      <Notifications badges={badges} />
      <Link to="/dashboard/support-chat" className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center text-stone-600 hover:bg-stone-100" data-testid="topbar-help-btn" aria-label="Hjälp"><HelpCircle size={19} /></Link>
      <UserMenu user={user} companyName={companyName} onLogout={onLogout} />
      <CommandSearch open={open} setOpen={setOpen} projects={projects} />
    </header>
  );
};
